import type { Router } from 'vue-router';
import { supabase } from '@/services/supabaseClient';
import { useUserStore } from '@/stores/useUserStore';

const PUBLIC_PATHS = new Set(['/login', '/auth/callback', '/landing', '/']);
const EXPIRY_GRACE_SECONDS = 60;
const AUTH_GUARD_TIMEOUT_MS = 8000;
const DEFINITIVE_REFRESH_ERROR_CODES = new Set([
  'refresh_token_not_found',
  'refresh_token_already_used',
  'session_expired',
  'invalid_grant',
]);

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('auth guard timeout')), timeoutMs);
    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });
}

function isDefinitiveRefreshFailure(error: unknown): boolean {
  const err = error as { code?: unknown; message?: unknown } | null;
  const code = String(err?.code ?? '').trim().toLowerCase();
  if (code && DEFINITIVE_REFRESH_ERROR_CODES.has(code)) return true;

  const message = String(err?.message ?? '').toLowerCase();
  return (
    message.includes('invalid refresh token')
    || message.includes('refresh token not found')
    || message.includes('refresh token has expired')
    || message.includes('session expired')
    || message.includes('already used')
  );
}

/**
 * Guards all non-public routes using the @supabase/ssr session.
 * Unauthenticated users are redirected to /login.
 */
export function applyAuthMiddleware(router: Router): void {
  router.beforeEach(async (to) => {
    if (PUBLIC_PATHS.has(to.path)) return true;

    const userStore = useUserStore();
    try {
      let session = userStore.session;
      if (!session) {
        try {
          const { data, error: sessionError } = await withTimeout(
            supabase.auth.getSession(),
            AUTH_GUARD_TIMEOUT_MS
          );
          if (sessionError || !data.session) {
            if (!userStore.user) return '/login';
          } else {
            session = data.session;
            userStore.session = data.session;
            if (!userStore.user && data.session.user) {
              userStore.user = data.session.user;
            }
          }
        } catch (sessionFetchError) {
          console.warn('[router auth guard session fallback]', sessionFetchError);
          if (!userStore.user) return '/login';
        }
      }

      const expiresAt = session?.expires_at ?? 0;
      const now = Math.floor(Date.now() / 1000);
      if (session && expiresAt > 0 && expiresAt <= now + EXPIRY_GRACE_SECONDS) {
        const { data: refreshed, error: refreshError } = await withTimeout(
          supabase.auth.refreshSession(),
          AUTH_GUARD_TIMEOUT_MS
        );
        if (refreshError || !refreshed.session) {
          // Mobile resilience: avoid destructive logout on transient network/lock errors.
          // Force logout only on definitive refresh-token failures.
          if (isDefinitiveRefreshFailure(refreshError)) {
            await supabase.auth.signOut().catch(() => { });
            userStore.user = null;
            userStore.session = null;
            return '/login';
          }
          console.warn('[router auth guard refresh fallback]', refreshError);
          if (session?.user || userStore.user) return true;
          return '/login';
        }
        session = refreshed.session;
        userStore.session = refreshed.session;
        userStore.user = refreshed.session.user;
      }

      // Do not block Ionic navigation on a network-bound getUser() call.
      // Keep the guard fast and based on local session state.
      if (!userStore.user && session?.user) {
        userStore.user = session.user;
      }

      return true;
    } catch (e) {
      console.error('[router auth guard]', e);
      if (userStore.user) return true;
      return '/login';
    }
  });
}
